import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { DollarSign, Users, TrendingUp, TrendingDown, Calendar, Activity, AlertCircle } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n || 0);

function SparkBars({ buckets }) {
  if (!buckets?.length) return null;
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <div className="flex items-end gap-0.5 h-8 mt-2" data-testid="pulse-sparkline">
      {buckets.map((b) => {
        const h = (b.count / max) * 100;
        return (
          <div
            key={b.date}
            title={`${b.date}: ${b.count}`}
            className="flex-1 bg-[#C85A42]/70 hover:bg-[#C85A42] rounded-sm transition-all min-h-[2px]"
            style={{ height: `${Math.max(h, 4)}%` }}
          />
        );
      })}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, color = 'text-[#C85A42]', testId, children }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
              <Icon className={`w-3.5 h-3.5 ${color}`} />
              <span>{label}</span>
            </div>
            <div className="mt-2 text-2xl font-bold" data-testid={testId}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function AdminPulseWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    adminAPI
      .getPulse()
      .then((res) => alive && setData(res.data))
      .catch(() => alive && setError('Could not load pulse'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pulse-loading">
        {['mrr', 'subscribers', 'signups', 'expiring'].map((k) => (
          <Skeleton key={`pulse-skeleton-${k}`} className="h-28" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card><CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> {error || 'No data'}
      </CardContent></Card>
    );
  }

  const trendPositive = (data.signup_trend_pct || 0) >= 0;
  const TrendIcon = trendPositive ? TrendingUp : TrendingDown;
  const trendColor = trendPositive ? 'text-emerald-600' : 'text-red-500';

  return (
    <div className="space-y-3" data-testid="admin-pulse-widget">
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 text-[#C85A42]" />
        <h2 className="font-semibold">Business Pulse</h2>
        <span className="text-xs text-muted-foreground ml-auto">Live · refreshed at {new Date(data.generated_at).toLocaleTimeString()}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* MRR */}
        <Tile
          icon={DollarSign}
          label="MRR"
          value={fmt(data.mrr)}
          sub={`ARR: ${fmt(data.arr)}`}
          testId="pulse-mrr"
        />

        {/* Active subscribers + monthly/annual split */}
        <Tile
          icon={Users}
          label="Active Subscribers"
          value={data.active_subscribers.total}
          sub={`${data.active_subscribers.monthly} monthly · ${data.active_subscribers.annual} annual${data.active_subscribers.legacy ? ` · ${data.active_subscribers.legacy} legacy` : ''}`}
          color="text-indigo-500"
          testId="pulse-active-subscribers"
        >
          {data.active_subscribers.total > 0 && (
            <div className="mt-3">
              <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                <div
                  className="bg-[#2F4F4F]"
                  style={{ width: `${data.monthly_vs_annual_pct.monthly}%` }}
                  title={`Monthly: ${data.monthly_vs_annual_pct.monthly}%`}
                />
                <div
                  className="bg-[#C85A42]"
                  style={{ width: `${data.monthly_vs_annual_pct.annual}%` }}
                  title={`Annual: ${data.monthly_vs_annual_pct.annual}%`}
                />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>Monthly {data.monthly_vs_annual_pct.monthly}%</span>
                <span>Annual {data.monthly_vs_annual_pct.annual}%</span>
              </div>
            </div>
          )}
        </Tile>

        {/* Last-7-day signups with trend + sparkline */}
        <Tile
          icon={TrendIcon}
          label="Signups (last 7d)"
          value={data.signups_last_7d}
          sub={
            <span className={trendColor}>
              {data.signup_trend_pct >= 0 ? '+' : ''}{data.signup_trend_pct}% vs prev. 7d
            </span>
          }
          color={trendColor}
          testId="pulse-signups-7d"
        >
          <SparkBars buckets={data.daily_signups} />
        </Tile>

        {/* Renewals coming up */}
        <Tile
          icon={Calendar}
          label="Expiring (next 14d)"
          value={data.expiring_soon_14d}
          sub={data.expiring_soon_14d > 0 ? 'Renewal opportunities' : 'No renewals due'}
          color="text-amber-500"
          testId="pulse-expiring-soon"
        />
      </div>
    </div>
  );
}
